<template>
  <div class="table-column-customizer">
    <!-- Trigger Button -->
    <button 
      class="btnGcn btnGcn-secondary btnGcn-sm column-customizer-btn" 
      @click="openModal" 
      title="Personalizar columnas"
    >
      <span class="material-icons-outlined">view_column</span>
      Configurar columnas
    </button>

    <!-- Modal -->
    <Modal :show="showModal" title="Personalizar Columnas" @close="closeModal" size="md">
      <template #body>
        <div class="column-customizer-content">
          <p class="help-text">
            Personaliza qué columnas mostrar y su orden. Existen columnas obligatorias que no se pueden ocultar ni reordenar.
          </p>
          
          <div class="columns-list">
            <!-- Comment out mandatory columns display for now -->
            <div 
              v-for="(column, index) in workingColumns" 
              v-if="!column.mandatory"
              :key="column.key"
              class="column-item"
              :class="{ 
                'mandatory': column.mandatory,
                'disabled': column.mandatory,
                'dragging': draggedIndex === index 
              }"
              :draggable="!column.mandatory"
              @dragstart="handleDragStart(index, $event)"
              @dragover="handleDragOver(index, $event)"
              @drop="handleDrop(index, $event)"
              @dragend="handleDragEnd"
            >
              <div class="column-item-content">
                <!-- Drag Handle -->
                <span 
                  v-if="!column.mandatory" 
                  class="drag-handle material-icons-outlined"
                  title="Arrastrar para reordenar"
                >
                  drag_indicator
                </span>
                <span v-else class="drag-handle-placeholder"></span>

                <!-- Visibility Checkbox -->
                <input 
                  type="checkbox" 
                  :id="`col-${column.key}`"
                  v-model="column.visible" 
                  :disabled="column.mandatory"
                  class="column-checkbox"
                />
                
                <!-- Column Label -->
                <label 
                  :for="`col-${column.key}`" 
                  class="column-label"
                  :class="{ 'mandatory-label': column.mandatory }"
                >
                  {{ column.label }}
                  <span v-if="column.mandatory" class="mandatory-indicator">(Obligatoria)</span>
                </label>

                <!-- Reorder Buttons -->
                <div v-if="!column.mandatory" class="reorder-buttons">
                  <button 
                    class="btnGcn btnGcn-link btnGcn-xs"
                    @click="moveUp(index)"
                    :disabled="index === 0 || isFirstNonMandatory(index)"
                    title="Mover arriba"
                  >
                    <span class="material-icons-outlined">keyboard_arrow_up</span>
                  </button>
                  <button 
                    class="btnGcn btnGcn-link btnGcn-xs"
                    @click="moveDown(index)"
                    :disabled="index === workingColumns.length - 1"
                    title="Mover abajo"
                  >
                    <span class="material-icons-outlined">keyboard_arrow_down</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="customizer-actions">
            <button class="btnGcn btnGcn-secondary btnGcn-sm" @click="selectAll">
              <span class="material-icons-outlined">check_box</span>
              Marcar todas
            </button>
            <button class="btnGcn btnGcn-secondary btnGcn-sm" @click="deselectAll">
              <span class="material-icons-outlined">check_box_outline_blank</span>
              Desmarcar todas
            </button>
          </div>
        </div>
      </template>
      
      <template #footer>
        <button class="btnGcn custom-btnGcn-outline" @click="closeModal">
          Cancelar
        </button>
        <button class="btnGcn custom-btnGcn" @click="applyChanges">
          Aplicar Cambios
        </button>
      </template>
    </Modal>
  </div>
</template>

<script>
import Modal from '@/features/portfolio/current/Modal.vue';

export default {
  name: 'TableColumnCustomizer',
  components: {
    Modal,
  },
  props: {
    columns: {
      type: Array,
      required: true,
    },
    storageKey: {
      type: String,
      required: true,
    }
  },
  data() {
    return {
      showModal: false,
      workingColumns: [],
      draggedIndex: null,
    };
  },
  created() {
    this.initializeColumns();
  },
  methods: {
    initializeColumns() {
      const savedConfig = this.loadConfiguration();
      
      if (savedConfig && savedConfig.length > 0) {
        this.workingColumns = this.mergeWithSavedConfig(savedConfig);
      } else {
        this.workingColumns = JSON.parse(JSON.stringify(this.columns));
      }
    },

    mergeWithSavedConfig(savedConfig) {
      const merged = [];
      
      savedConfig.forEach(savedCol => {
        const currentCol = this.columns.find(col => col.key === savedCol.key);
        if (currentCol) {
          merged.push({
            ...currentCol,
            visible: savedCol.visible !== undefined ? savedCol.visible : currentCol.visible
          });
        }
      });
      
      this.columns.forEach(currentCol => {
        if (!merged.find(col => col.key === currentCol.key)) {
          merged.push({ ...currentCol });
        }
      });
      
      return merged;
    },

    loadConfiguration() {
      try {
        const saved = localStorage.getItem(`tableColumns_${this.storageKey}`);
        return saved ? JSON.parse(saved) : null;
      } catch (error) {
        console.warn('Error loading column configuration:', error);
        return null;
      }
    },

    saveConfiguration() {
      try {
        const configToSave = this.workingColumns.map(col => ({
          key: col.key,
          visible: col.visible
        }));
        localStorage.setItem(`tableColumns_${this.storageKey}`, JSON.stringify(configToSave));
      } catch (error) {
        console.warn('Error saving column configuration:', error);
      }
    },

    openModal() {
      this.workingColumns = JSON.parse(JSON.stringify(this.getActiveConfiguration()));
      this.showModal = true;
      // Add custom class to modal container after next tick to ensure DOM is updated
      this.$nextTick(() => {
        const modalContainer = document.querySelector('.modal-container-gcn');
        if (modalContainer) {
          modalContainer.classList.add('column-customizer-modal');
        }
      });
    },

    closeModal() {
      // Remove custom class from modal container
      const modalContainer = document.querySelector('.modal-container-gcn');
      if (modalContainer) {
        modalContainer.classList.remove('column-customizer-modal');
      }
      this.showModal = false;
      this.draggedIndex = null;
    },

    getActiveConfiguration() {
      const savedConfig = this.loadConfiguration();
      return savedConfig ? this.mergeWithSavedConfig(savedConfig) : this.workingColumns;
    },

    applyChanges() {
      this.saveConfiguration();
      this.$emit('columns-changed', this.workingColumns);
      this.closeModal();
    },

    selectAll() {
      this.workingColumns.forEach(column => {
        if (!column.mandatory) {
          column.visible = true;
        }
      });
    },

    deselectAll() {
      this.workingColumns.forEach(column => {
        if (!column.mandatory) {
          column.visible = false;
        }
      });
    },

    handleDragStart(index, event) {
      if (this.workingColumns[index].mandatory) {
        event.preventDefault();
        return;
      }
      this.draggedIndex = index;
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/html', event.target);
    },

    handleDragOver(index, event) {
      if (this.draggedIndex === null || this.workingColumns[index].mandatory) {
        return;
      }
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
    },

    handleDrop(index, event) {
      if (this.draggedIndex === null || this.workingColumns[index].mandatory || this.draggedIndex === index) {
        return;
      }
      event.preventDefault();
      
      if (this.workingColumns[this.draggedIndex].mandatory) {
        return;
      }

      this.moveItem(this.draggedIndex, index);
      this.draggedIndex = null;
    },

    handleDragEnd() {
      this.draggedIndex = null;
    },

    moveUp(index) {
      if (index > 0 && !this.isFirstNonMandatory(index)) {
        let targetIndex = index - 1;
        while (targetIndex >= 0 && this.workingColumns[targetIndex].mandatory) {
          targetIndex--;
        }
        if (targetIndex >= 0) {
          this.moveItem(index, targetIndex);
        }
      }
    },

    moveDown(index) {
      if (index < this.workingColumns.length - 1) {
        this.moveItem(index, index + 1);
      }
    },

    moveItem(fromIndex, toIndex) {
      const item = this.workingColumns.splice(fromIndex, 1)[0];
      this.workingColumns.splice(toIndex, 0, item);
    },

    isFirstNonMandatory(index) {
      for (let i = 0; i < index; i++) {
        if (!this.workingColumns[i].mandatory) {
          return false;
        }
      }
      return true;
    }
  },

  watch: {
    columns: {
      deep: true,
      handler() {
        this.initializeColumns();
      }
    }
  }
};
</script>

<style scoped>
.table-column-customizer {
  display: inline-block;
}

.column-customizer-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.85em;
  padding: 6px 10px;
}

.column-customizer-btn .material-icons-outlined {
  font-size: 16px;
  margin-right: 0;
}

/* Custom modal styles for column customizer */
:deep(.modal-container-gcn.column-customizer-modal) {
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
}

:deep(.modal-container-gcn.column-customizer-modal .modal-body-gcn) {
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.column-customizer-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.help-text {
  margin-bottom: 20px;
  color: var(--color-text-secondary);
  font-size: 0.9em;
  line-height: 1.4;
  flex-shrink: 0;
}

.columns-list {
  border: 1px solid var(--color-outline);
  border-radius: 6px;
  overflow-y: auto;
  margin-bottom: 20px;
  max-height: 300px; /* Fixed height for the scrollable area */
  min-height: 200px;
}

.column-item {
  border-bottom: 1px solid var(--color-outline);
  background-color: var(--color-surface);
  transition: all 0.2s ease;
  cursor: move;
}

.column-item:last-child {
  border-bottom: none;
}

.column-item.mandatory {
  background-color: var(--color-active-surface);
  cursor: default;
}

.column-item.dragging {
  opacity: 0.5;
  transform: rotate(2deg);
}

.column-item:hover:not(.mandatory) {
  background-color: var(--color-active-surface);
}

.column-item-content {
  display: flex;
  align-items: center;
  padding: 12px;
  gap: 12px;
}

.drag-handle {
  color: var(--color-text-secondary);
  cursor: grab;
  font-size: 18px;
  width: 20px;
  text-align: center;
}

.drag-handle:active {
  cursor: grabbing;
}

.drag-handle-placeholder {
  width: 20px;
}

.column-checkbox {
  margin: 0;
  cursor: pointer;
}

.column-checkbox:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.column-label {
  flex: 1;
  margin: 0;
  cursor: pointer;
  font-size: 0.9em;
  color: var(--color-text-primary);
  display: flex;
  align-items: center;
  gap: 8px;
}

.mandatory-label {
  font-weight: 500;
  color: var(--color-text-secondary);
}

.mandatory-indicator {
  font-size: 0.8em;
  color: var(--color-text-secondary);
  font-style: italic;
}

.reorder-buttons {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.btnGcn-xs {
  padding: 2px 4px;
  font-size: 12px;
  line-height: 1;
  border: none;
  background: transparent;
  color: var(--color-text-secondary);
  cursor: pointer;
  border-radius: 2px;
  transition: all 0.15s ease;
}

.btnGcn-xs:hover:not(:disabled) {
  background-color: var(--color-outline);
  color: var(--color-text-primary);
}

.btnGcn-xs:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.btnGcn-xs .material-icons-outlined {
  font-size: 14px;
  margin: 0;
}

.customizer-actions {
  padding-top: 15px;
  border-top: 1px solid var(--color-outline);
  text-align: center;
  flex-shrink: 0;
  display: flex;
  gap: 10px;
  justify-content: center;
}

.customizer-actions .btnGcn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85em;
  padding: 6px 12px;
}

.customizer-actions .material-icons-outlined {
  font-size: 16px;
  margin-right: 0;
}

.custom-btnGcn {
  background-color: var(--color-highlight);
  color: var(--color-text-on-highlight);
  border: 1px solid var(--color-highlight);
  transition: all 0.2s ease;
  padding: 6px 12px;
  font-size: 0.85rem;
  cursor: pointer;
}

.custom-btnGcn:hover {
  background-color: var(--color-text-primary);
  color: var(--color-background);
  border-color: var(--color-text-primary);
  transform: translateY(-1px);
  box-shadow: 0 2px 8px var(--color-outline);
}

.custom-btnGcn-outline {
  background-color: transparent;
  color: var(--color-highlight);
  border: 1px solid var(--color-outline);
  padding: 6px 12px;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.custom-btnGcn-outline:hover {
  background-color: var(--color-surface);
  color: var(--color-text-primary);
  border-color: var(--color-highlight);
}

@media (max-width: 768px) {
  .column-item-content {
    padding: 8px;
    gap: 8px;
  }
  
  .column-label {
    font-size: 0.85em;
  }
  
  .reorder-buttons {
    display: none;
  }
}
</style> 
